import React, { useState, useEffect, useRef } from "react";

import useElementSelector from "./hooks/useElementSelector";
import useScreenshot from "./hooks/useScreenshot";
import { getRecentErrors } from "./hooks/useErrorBuffer";
import BugReportButton from "./BugReportButton";
import BugDialog from "./BugDialog";
import BugStatusSnackbar from "./BugStatusSnackbar";
import api from "../../services/api";
import isNativeApp from "../../utils/isNativeApp";

function BugReporterWidget() {
  const { state, startSelecting, selector } = useElementSelector();
  const { takeScreenshot } = useScreenshot();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bugId, setBugId] = useState(null);
  const [snackStatus, setSnackStatus] = useState("received");
  const [snackOpen, setSnackOpen] = useState(false);

  const pollingRef = useRef(null);

  // When element selection completes, take screenshot then open dialog
  useEffect(() => {
    if (state === "selected") {
      (async () => {
        const shot = await takeScreenshot();
        setScreenshot(shot);
        setDialogOpen(true);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const handleButtonClick = () => {
    startSelecting();
  };

  const handleSubmit = async (description) => {
    setLoading(true);
    try {
      let platform = "web";
      if (isNativeApp()) {
        try {
          const { Device } = await import(/* webpackIgnore: true */ "@capacitor/device");
          const info = await Device.getInfo();
          platform = info.platform === "ios" ? "ios" : "android";
        } catch (_) {
          // Device plugin unavailable — still in the native shell, so fall back
          // to a VALID enum value (web|ios|android), not "mobile".
          platform = "android";
        }
      }

      const payload = {
        description,
        platform,
        url: window.location.href,
        elementSelector: selector,
        screenshot,
        consoleErrors: getRecentErrors(),
      };

      const { data } = await api.post("/bug-reports", payload);
      setBugId(data.bugId);
      setSnackStatus("received");
      setSnackOpen(true);
      setDialogOpen(false);
      startPolling(data.bugId);
    } catch (_) {
      setSnackStatus("failed");
      setSnackOpen(true);
    } finally {
      setLoading(false);
      setDialogOpen(false);
    }
  };

  const startPolling = (id) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    let attempts = 0;
    const MAX_ATTEMPTS = 40; // ~2 min at 3s — don't poll a stuck report forever
    pollingRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/bug-reports/${id}`);
        // pending_review = Claude analyzed and sent to Telegram; stop polling and show message
        if (["pending_review", "escalated", "failed", "fixed"].includes(data.status)) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setSnackStatus(data.status);
          setSnackOpen(true);
          return;
        }
      } catch (_) {
        // silently ignore polling errors
      }
      if (attempts >= MAX_ATTEMPTS && pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }, 3000);
  };

  return (
    <>
      <BugReportButton onClick={handleButtonClick} />
      <BugDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        loading={loading}
        screenshot={screenshot}
        selector={selector}
      />
      <BugStatusSnackbar
        open={snackOpen}
        status={snackStatus}
        bugId={bugId}
        onClose={() => setSnackOpen(false)}
      />
    </>
  );
}

export default React.memo(BugReporterWidget);
